import { createContext, use } from 'react';
import type { ComponentType } from 'react';

export type ToastTone = 'success' | 'error' | 'warning' | 'info';
export type ToastPresentation = 'toast' | 'modal';

export interface ToastAction {
  readonly label: string;
  readonly onClick: () => void;
}

export interface ToastOptions {
  /** Overrides the default heading for the tone. */
  readonly title?: string;
  /** Custom icon component to render instead of the tone default icon. */
  readonly icon?: ComponentType<{ size?: number; strokeWidth?: number; className?: string }>;
  /** A single follow-up the notification offers, e.g. Retry or Undo. */
  readonly action?: ToastAction;
  /** Milliseconds before auto-dismiss; null pins it until dismissed. */
  readonly duration?: number | null;
  /**
   * How it is shown.
   *
   * 'toast' (the default) is a card in the top-right corner that does not block
   * the page. Routine progress and outcomes belong here: an export kicking off
   * is not a decision, and two centred modals stacking on top of each other
   * ("Exporting…" then "Export completed.") made the user dismiss the app twice
   * to get back to work.
   *
   * 'modal' is the centred, blocking card. Reserve it for something that must
   * be read before continuing.
   */
  readonly presentation?: ToastPresentation;
}

export interface ToastContextValue {
  /** Returns the toast's id, so a progress message can be cleared by its result. */
  readonly notify: (tone: ToastTone, message: string, options?: ToastOptions) => number;
  readonly success: (message: string, options?: ToastOptions) => number;
  /** Accepts an unknown thrown value; never renders "[object Object]". */
  readonly error: (error: unknown, options?: ToastOptions) => number;
  readonly warning: (message: string, options?: ToastOptions) => number;
  readonly info: (message: string, options?: ToastOptions) => number;
  readonly dismiss: (id: number) => void;
}

export const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const context = use(ToastContext);
  if (!context) throw new Error('useToast must be used inside <ToastProvider>.');
  return context;
}

export function useOptionalToast(): ToastContextValue | null {
  return use(ToastContext);
}
